<script setup>
import { onMounted, ref, watch } from "vue";

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  checkoutUrl: {
    type: String,
    default: "",
  },
  consumerKey: {
    type: String,
    default: "default",
  },
});

const emit = defineEmits(["update:open", "completed", "failed"]);

const route = useRoute();
const router = useRouter();
const handledSessionId = ref("");

/** Vue Router 的 query 可能是 string | string[] */
function firstQueryParam(val) {
  if (val == null || val === "") return "";
  return Array.isArray(val) ? String(val[0] || "") : String(val);
}

const redirectToCheckout = () => {
  if (!import.meta.client || !props.open) return;

  if (!props.checkoutUrl) {
    emit("update:open", false);
    emit("failed", "Stripe checkout url invalid");
    return;
  }

  emit("update:open", false);
  window.location.assign(props.checkoutUrl);
};

function buildReturnQuery(query) {
  const {
    stripe_session_id: _sid,
    stripe_checkout_consumer: _consumer,
    interval: _interval,
    ...restQuery
  } = query;
  const cycle = firstQueryParam(query.cycle) || firstQueryParam(query.interval);

  if (!cycle) {
    return restQuery;
  }

  return {
    ...restQuery,
    cycle,
  };
}

function buildReplacePath() {
  const currentPath = import.meta.client
    ? window.location.pathname || route.path || "/"
    : route.path || "/";

  if (currentPath === "/") {
    return currentPath;
  }

  return currentPath.endsWith("/") ? currentPath : `${currentPath}/`;
}

const handleCheckoutReturn = async (sessionId) => {
  if (!sessionId || handledSessionId.value === sessionId) {
    return;
  }

  handledSessionId.value = sessionId;

  try {
    await router.replace({
      path: buildReplacePath(),
      query: buildReturnQuery(route.query),
    });
  } catch (error) {
    console.error("Clear stripe session query failed:", error);
  }

  emit("completed");
};

function shouldConsumeStripeReturn() {
  if (!import.meta.client) return false;
  const sid = firstQueryParam(route.query.stripe_session_id);
  if (!sid) return false;

  const consumer = firstQueryParam(route.query.stripe_checkout_consumer);
  if (!consumer) {
    return String(props.consumerKey || "default") === "pricing";
  }
  return consumer === String(props.consumerKey || "default");
}

/** Stripe 托管页整页跳回：须在客户端执行；避免 SSR 时 immediate watch 直接 return 后客户端不再触发 */
const consumeStripeReturn = () => {
  if (!shouldConsumeStripeReturn()) return;
  const sid = firstQueryParam(route.query.stripe_session_id);
  if (sid) {
    handleCheckoutReturn(sid);
  }
};

watch(
  () => props.open,
  (open) => {
    if (open) {
      redirectToCheckout();
    }
  },
  { immediate: true }
);

onMounted(() => {
  consumeStripeReturn();
});

watch(
  () => route.query.stripe_session_id,
  () => {
    consumeStripeReturn();
  }
);
</script>

<template>
  <span class="hidden"></span>
</template>